import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const LoginForm = ({ onSubmit, loading = false, transparent = false }) => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await signIn(formData?.email, formData?.password);

      if (error) {
        setErrors({ 
          general: error?.message || 'Invalid credentials. Please try again.' 
        });
        return;
      }

      if (data?.user) {
        if (onSubmit) {
          onSubmit(data?.user);
        }
        navigate('/user-account-dashboard');
      }
    } catch (error) {
      setErrors({ general: 'An error occurred. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* General Error Message */}
      {errors?.general && (
        <div className={`${transparent ? 'bg-red-500/20 border-red-400/30' : 'bg-error/10 border-error/20'} backdrop-blur-sm border rounded-lg p-4 animate-fade-in`}>
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={16} className={transparent ? "text-red-200" : "text-error"} />
            <p className={`font-body text-sm ${transparent ? 'text-red-100' : 'text-error'}`}>{errors?.general}</p>
          </div>
        </div>
      )}

      {/* Email Field */}
      <Input
        label="Email Address"
        type="email"
        name="email"
        value={formData?.email}
        onChange={handleInputChange}
        placeholder="Enter your email address"
        error={errors?.email}
        required
        disabled={isSubmitting || loading}
        className="w-full"
        transparent={transparent}
      />

      {/* Password Field */}
      <div className="relative">
        <Input
          label="Password"
          type={showPassword ? 'text' : 'password'}
          name="password"
          value={formData?.password}
          onChange={handleInputChange}
          placeholder="Enter your password"
          error={errors?.password}
          required
          disabled={isSubmitting || loading}
          className="w-full pr-12"
          transparent={transparent}
        />

        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className={`absolute right-3 top-9 ${transparent ? 'text-white/80 hover:text-white' : 'text-muted-foreground hover:text-foreground'} transition-colors`}
          disabled={isSubmitting || loading}
        >
          <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
        </button>
      </div>

      {/* Remember Me Checkbox */}
      <div className="flex items-center justify-between">
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            name="rememberMe"
            checked={formData?.rememberMe}
            onChange={handleInputChange}
            disabled={isSubmitting || loading}
            className={`w-4 h-4 rounded ${transparent ? 'bg-white/20 border-white/30 text-white focus:ring-white/50' : 'bg-background border-input text-primary focus:ring-ring'} focus:ring-2 focus:ring-offset-0 transition-colors`}
          />
          <span className={`font-body text-sm ${transparent ? 'text-white/90 drop-shadow' : 'text-foreground'}`}>
            Remember me
          </span>
        </label>

        <button
          type="button"
          onClick={() => navigate('/register')}
          className={`font-body text-sm ${transparent ? 'text-white/90 hover:text-white drop-shadow' : 'text-primary hover:text-primary/80'} transition-colors`}
          disabled={isSubmitting || loading}
        >
          Forgot password?
        </button>
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        variant={transparent ? "default" : "default"}
        size="lg"
        fullWidth
        loading={isSubmitting || loading}
        disabled={isSubmitting || loading}
        iconName="LogIn"
        iconPosition="left"
        className={transparent ? "bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 text-white shadow-lg" : ""}
      >
        {isSubmitting ? 'Signing In...' : 'Sign In'}
      </Button>
    </form>
  );
};

export default LoginForm;